import { useEffect, useMemo, useRef, useState } from 'react';
import { categories } from '../../data/milestones';
import { useBabyMilestones, useLeaps } from '../../data/queries';
import { rowToLeap, rowToMilestone } from '../../data/derive';
import type { Baby, Leap, Milestone } from '../../data/types';
import { ControlBar, passesFilter, type Filter, type GroupBy } from '../milestones/milestoneControls';
import { useT } from '../../i18n';

interface Props {
  baby: Baby;
  onOpenDetail?: (id: string) => void;
  /** Legacy ignored. */
  accent?: string;
  accentInk?: string;
  onOpenProfile?: () => void;
  initialGroupBy?: GroupBy;
  /** Hide the built-in title when embedded under another screen's header. */
  showHeader?: boolean;
  /** Render the built-in filter/group dropdowns (standalone). Off when the
   *  parent screen already owns them and drives `filter`/`groupBy`. */
  showControls?: boolean;
  /** Controlled filter/group-by (when the parent owns the dropdowns). */
  filter?: Filter;
  groupBy?: GroupBy;
}

export function Timeline({
  baby,
  onOpenDetail,
  initialGroupBy = 'category',
  showHeader = true,
  showControls = true,
  filter: filterProp,
  groupBy: groupByProp,
}: Props) {
  const t = useT();
  const today = baby.ageMonths;
  const monthsCount = 20;
  const monthW = 60;
  const totalW = monthsCount * monthW;
  const padL = 96;
  const rowH = 52;
  const groupGap = 14;
  const babyKey = baby.id ? `babysteps.tl.${baby.id}` : null;

  // Filter/group-by are controlled when the parent passes them (embedded in
  // the Milestones screen); otherwise this screen owns its own state.
  const [internalFilter, setInternalFilterState] = useState<Filter>(() => {
    if (typeof sessionStorage !== 'undefined' && babyKey) {
      const f = sessionStorage.getItem(`${babyKey}.filter`);
      if (f === 'open' || f === 'achieved' || f === 'all') return f as Filter;
    }
    return 'open';
  });
  const [internalGroupBy, setInternalGroupByState] = useState<GroupBy>(() => {
    if (typeof sessionStorage !== 'undefined' && babyKey) {
      const g = sessionStorage.getItem(`${babyKey}.groupBy`);
      if (g === 'date' || g === 'category' || g === 'status') return g as GroupBy;
    }
    return initialGroupBy;
  });
  const filter = filterProp ?? internalFilter;
  const groupBy = groupByProp ?? internalGroupBy;

  const setInternalFilter = (f: Filter) => {
    if (typeof sessionStorage !== 'undefined' && babyKey) sessionStorage.setItem(`${babyKey}.filter`, f);
    setInternalFilterState(f);
  };
  const setInternalGroupBy = (g: GroupBy) => {
    if (typeof sessionStorage !== 'undefined' && babyKey) sessionStorage.setItem(`${babyKey}.groupBy`, g);
    setInternalGroupByState(g);
  };

  const msQ    = useBabyMilestones(baby.id ?? null);
  const leapsQ = useLeaps();

  const allMilestones: Milestone[] = useMemo(
    () => (msQ.data?.milestones ?? []).map(rowToMilestone),
    [msQ.data],
  );
  const milestones: Milestone[] = useMemo(
    () => allMilestones.filter((m) => passesFilter(m.status, filter)),
    [allMilestones, filter],
  );
  const leaps: Leap[] = useMemo(
    () => (leapsQ.data ?? []).map(rowToLeap),
    [leapsQ.data],
  );

  let grouped: Array<{
    key: string;
    label: string;
    sublabel?: string;
    leap?: Leap;
    items: Milestone[];
  }> = [];
  if (groupBy === 'category') {
    grouped = categories
      .map((cat) => ({
        key: cat,
        label: cat,
        items: milestones.filter((m) => m.cat === cat),
      }))
      .filter((g) => g.items.length > 0);
  } else if (groupBy === 'leap') {
    grouped = leaps
      .map((l) => ({
        key: `leap-${l.id}`,
        label: `Leap ${l.id}`,
        sublabel: l.short,
        leap: l,
        items: milestones.filter((m) => m.leap === l.id),
      }))
      .filter((g) => g.items.length > 0);
  } else {
    grouped = [
      {
        key: 'all',
        label: 'All milestones',
        items: [...milestones].sort((a, b) => a.medianWeeks - b.medianWeeks || a.startWeeks - b.startWeeks),
      },
    ];
  }

  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Restore scrollLeft and scrollTop on mount
  useEffect(() => {
    if (scrollRef.current) {
      const savedLeft = typeof sessionStorage !== 'undefined' && babyKey ? sessionStorage.getItem(`${babyKey}.scrollLeft`) : null;
      const savedTop = typeof sessionStorage !== 'undefined' && babyKey ? sessionStorage.getItem(`${babyKey}.scrollTop`) : null;
      if (savedLeft !== null) {
        scrollRef.current.scrollLeft = Number(savedLeft);
        if (savedTop !== null) scrollRef.current.scrollTop = Number(savedTop);
      } else {
        scrollRef.current.scrollLeft = Math.max(0, padL + today * monthW - 160);
      }
    }
  }, [today, babyKey, padL, monthW]);

  // Track scroll position of the scroll container
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof sessionStorage === 'undefined' || !babyKey) return;

    const savedLeft = sessionStorage.getItem(`${babyKey}.scrollLeft`);
    const savedTop = sessionStorage.getItem(`${babyKey}.scrollTop`);
    if (savedLeft !== null) el.scrollLeft = Number(savedLeft);
    if (savedTop !== null) el.scrollTop = Number(savedTop);

    const handleScroll = () => {
      sessionStorage.setItem(`${babyKey}.scrollLeft`, String(el.scrollLeft));
      sessionStorage.setItem(`${babyKey}.scrollTop`, String(el.scrollTop));
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
    };
  }, [babyKey]);

  // Track scroll position of the window
  useEffect(() => {
    if (typeof sessionStorage === 'undefined' || !babyKey) return;
    const savedY = Number(sessionStorage.getItem(`${babyKey}.windowScrollY`) ?? 0);
    if (savedY > 0) {
      window.scrollTo(0, savedY);
      const timer = setTimeout(() => window.scrollTo(0, savedY), 0);
      return () => clearTimeout(timer);
    }
  }, [babyKey]);

  useEffect(() => {
    if (typeof sessionStorage === 'undefined' || !babyKey) return;
    const handleWindowScroll = () => {
      sessionStorage.setItem(`${babyKey}.windowScrollY`, String(window.scrollY));
    };

    window.addEventListener('scroll', handleWindowScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleWindowScroll);
    };
  }, [babyKey]);

  // Cap the chart's height to the space between its top and the bottom of the
  // viewport, leaving room for the legend and (on mobile) the bottom nav. The
  // month header stays pinned (sticky) and the milestone rows scroll inside.
  const [maxChartH, setMaxChartH] = useState<number>();
  useEffect(() => {
    const measure = () => {
      const el = scrollRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const isWide = window.matchMedia('(min-width: 768px)').matches;
      const bottomReserve = (isWide ? 24 : 104) + 44; // nav + safe area + legend row
      setMaxChartH(Math.max(220, window.innerHeight - top - bottomReserve));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [showHeader, showControls]);

  return (
    <div className={[
      "flex flex-col gap-4",
      showHeader ? "pb-24" : "pb-0"
    ].join(' ')}>
      {/* Heading */}
      {showHeader && (
        <header>
          <p className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">{t('timeline.eyebrow', 'Timeline')}</p>
          <h1 className="mt-1 font-serif text-[28px] nav:text-3xl leading-tight tracking-tight text-ink">
            {t('timeline.subtitle', 'First year, at a glance')}
          </h1>
        </header>
      )}

      {/* Filter + Group-by — only when this screen owns the controls */}
      {showControls && (
        <ControlBar
          filter={filter}
          setFilter={setInternalFilter}
          groupBy={groupBy}
          setGroupBy={setInternalGroupBy}
        />
      )}

      {/* Chart — full-bleed on mobile so the scroll feels natural. Height is
          capped so the month header pins at the top and the rows scroll. */}
      <div
        ref={scrollRef}
        style={{ maxHeight: maxChartH }}
        className="
          relative overflow-auto overscroll-contain rounded-2xl border border-line bg-surface
          -mx-5 nav:-mx-0 px-0
        "
      >
        <div className="relative" style={{ width: padL + totalW + 24 }}>
          {/* Month header */}
          <div
            className="sticky top-0 z-10 grid h-10 items-end border-b border-hairline bg-surface"
            style={{ gridTemplateColumns: `${padL}px repeat(${monthsCount}, ${monthW}px)` }}
          >
            <div />
            {Array.from({ length: monthsCount }, (_, i) => (
              <div
                key={i}
                className={[
                  'pb-2 pl-1 text-[11px] font-medium tracking-wide text-ink-3',
                  i === 0 ? '' : 'border-l border-hairline',
                ].join(' ')}
              >
                {i === 0 ? t('journey.birth', 'Birth').toLowerCase() : `${i}${t('common.monthShort', 'mo')}`}
              </div>
            ))}
          </div>

          {/* Today line */}
          <div
            className="pointer-events-none absolute z-5 w-0 border-l-[1.5px] border-dashed border-accent"
            style={{ left: padL + today * monthW, top: 40, bottom: 0 }}
          >
            <div className="absolute -top-0.5 -left-5.5 rounded-lg bg-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
              {t('today.title', 'Today')}
            </div>
          </div>

          {/* Group rows */}
          {grouped.map((g, gi) => (
            <div
              key={g.key}
              className="relative"
              style={{ paddingTop: gi === 0 ? 10 : groupGap }}
            >
              <div
                className="sticky left-0 z-3 bg-surface py-2 pl-6 pr-3 font-serif text-[13.5px] leading-snug tracking-tight text-ink"
                style={{ width: padL }}
              >
                {g.label}
                {g.sublabel && (
                  <div className="mt-px font-sans text-[10.5px] font-medium tracking-wide text-ink-3">
                    {g.sublabel}
                  </div>
                )}
              </div>
              {groupBy === 'leap' && g.leap && (
                <div
                  className="pointer-events-none absolute inset-y-0 z-2 w-0 border-l border-dotted"
                  style={{
                    left: padL + (g.leap.week / 4.345) * monthW,
                    borderColor: 'color-mix(in oklch, var(--color-accent) 40%, transparent)',
                  }}
                />
              )}
              {g.items.map((m) => {
                const left = padL + m.start * monthW;
                const w = (m.end - m.start) * monthW;
                const medianLeft = padL + m.median * monthW;
                const isDone = m.status === 'done';
                const isNow = m.status === 'now';
                const barBg = isDone ? 'var(--color-accent)' : isNow ? 'var(--color-amber)' : 'var(--color-grey)';

                const formatDate = (iso: string | null) => {
                  if (!iso) return '';
                  try {
                    return new Date(iso + 'T00:00:00Z').toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      timeZone: 'UTC',
                    });
                  } catch {
                    return iso;
                  }
                };

                const getSecondLine = () => {
                  if (m.status === 'done') {
                    const dStr = m.achievedDate ? formatDate(m.achievedDate) : '';
                    return dStr ? t('timeline.achievedOn', 'Achieved on {date}').replace('{date}', dStr) : t('status.achieved', 'Achieved');
                  }
                  if (m.status === 'now') {
                    const dStr = m.startedAt ? formatDate(m.startedAt) : '';
                    return dStr ? t('timeline.startedOn', 'Started on {date}').replace('{date}', dStr) : t('status.inProgress', 'In progress');
                  }

                  // Upcoming
                  const startW = Math.round(m.startWeeks);
                  const medianW = Math.round(m.medianWeeks);
                  const bandLo = Math.round(Math.max(0, m.medianWeeks - m.stdDevWeeks));
                  const bandHi = Math.round(m.medianWeeks + m.stdDevWeeks);
                  const flagW = Math.round(m.earlyFlagWeeks);

                  return t('timeline.upcomingDetail', 'Start: wk {start} · Median: wk {median} · Most: wk {bandLo}–{bandHi} · Flag: wk {flag}')
                    .replace('{start}', String(startW))
                    .replace('{median}', String(medianW))
                    .replace('{bandLo}', String(bandLo))
                    .replace('{bandHi}', String(bandHi))
                    .replace('{flag}', String(flagW));
                };

                return (
                  <div
                    key={m.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onOpenDetail?.(m.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onOpenDetail?.(m.id);
                      }
                    }}
                    className="relative cursor-pointer transition-colors hover:bg-grey-soft/10"
                    style={{ height: rowH }}
                  >
                    {/* Baseline */}
                    <div
                      className="absolute h-px bg-hairline pointer-events-none"
                      style={{ left: padL, right: 24, top: rowH / 2 }}
                    />
                    {/* Bar */}
                    <div
                      className="absolute box-border flex flex-col justify-center overflow-hidden rounded-xl px-3 py-1 pointer-events-none"
                      style={{
                        left,
                        top: 4,
                        height: rowH - 8,
                        width: w,
                        background: isDone ? barBg : 'var(--color-bg)',
                        border: `1.5px solid ${barBg}`,
                      }}
                    >
                      <span
                        className="overflow-hidden truncate whitespace-nowrap text-[10.5px] font-bold tracking-wide"
                        style={{ color: isDone ? 'white' : 'var(--color-ink)' }}
                      >
                        {m.name}
                      </span>
                      <span
                        className="overflow-hidden truncate whitespace-nowrap text-[8.5px] tracking-wide font-medium mt-0.5"
                        style={{ color: isDone ? 'rgba(255, 255, 255, 0.85)' : 'var(--color-ink-3)' }}
                      >
                        {getSecondLine()}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          <div className="h-10" />
        </div>
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-4 px-5 text-xs font-medium text-ink-2">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-1.5 w-3.5 rounded-sm bg-accent" />
          {t('status.achieved', 'Achieved')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="box-border block h-1.5 w-3.5 rounded-sm border-[1.5px] border-amber" />
          {t('status.inProgress', 'In progress')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="box-border block h-1.5 w-3.5 rounded-sm border-[1.5px] border-grey" />
          {t('status.upcoming', 'Upcoming')}
        </span>
      </div>
    </div>
  );
}
